import { Effect, PostProcess } from '@babylonjs/core';
import { Level } from '../Level';

export class UnderwaterEffect {
  private underwaterPostProcess: PostProcess | null = null;
  private isUnderwater: boolean = false;

  constructor(public level: Level) {
    this.prepareUnderwaterShader();
    this.level.game.scene.onBeforeRenderObservable.add(() => this.checkUnderwaterStatus());
  }

  private prepareUnderwaterShader(): void {
    // shader mit procedural noise funktion
    Effect.ShadersStore['underwaterFragmentShader'] = `
            precision highp float;

            varying vec2 vUV;
            uniform sampler2D textureSampler;
            uniform float time;
            uniform vec3 waterColor;
            uniform float distortionStrength;
            uniform float fogDensity;

            // Hash-Funktion für Random-Werte
            float hash(vec2 p) {
                p = fract(p * vec2(123.34, 456.21));
                p += dot(p, p + 45.32);
                return fract(p.x * p.y);
            }

            // 2D-Perlin-Noise
            float noise(vec2 p) {
                vec2 i = floor(p);
                vec2 f = fract(p);

                // Kantenmischung mit kubischer Glättung
                vec2 u = f * f * (3.0 - 2.0 * f);

                // Bilineare Interpolation der vier Ecken
                return mix(
                    mix(hash(i + vec2(0.0, 0.0)), hash(i + vec2(1.0, 0.0)), u.x),
                    mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x),
                    u.y
                );
            }

            // Fraktales Brownian Motion (FBM) für mehrere Noise-Ebenen
            float fbm(vec2 p) {
                float value = 0.0;
                float amplitude = 0.5;
                float frequency = 3.0;

                // Überlagere mehrere Noise-Ebenen
                for (int i = 0; i < 5; i++) {
                    value += amplitude * noise(p * frequency);
                    amplitude *= 0.5;
                    frequency *= 2.0;
                }

                return value;
            }

            // Luftblasen-Effekt
            float bubbles(vec2 p, float time) {
                float b = 0.0;
                // Mehrere sich bewegende Blasenebenen
                for (int i = 1; i < 4; i++) {
                    float speed = float(i) * 0.2;
                    float scale = float(i) * 2.0;
                    // Blasen steigen auf
                    vec2 bubblePos = vec2(p.x * scale, p.y * scale + time * speed);
                    float bubble = noise(bubblePos);
                    // Blasen Kontrast verstärken und Größe reduzieren
                    bubble = pow(bubble, 8.0) * 0.7;
                    b += bubble;
                }
                return b;
            }

            void main(void) {
                // Langsam bewegende Unterwasserverzerrung
                vec2 uvOffset1 = vec2(fbm(vUV + vec2(time * 0.01, time * 0.015)));
                vec2 uvOffset2 = vec2(fbm(vUV + vec2(time * -0.012, time * 0.01) + uvOffset1 * 0.4));

                // Kombination von Verzerrungsebenen für organischere Bewegung
                vec2 uvDistortion = (uvOffset1 + uvOffset2) * 0.5;
                uvDistortion = (uvDistortion * 2.0 - 1.0) * distortionStrength;

                // Verzerrte Texturkoordinaten anwenden
                vec2 distortedUV = vUV + uvDistortion;
                vec4 baseColor = texture2D(textureSampler, distortedUV);

                // Unterwasserfarbe subtil einmischen
                float waterMixFactor = 0.4;
                vec4 blueish = mix(baseColor, vec4(waterColor, 1.0), waterMixFactor);

                // Tiefeneffekt mit nicht-linearer Abschwächung
                float depth = 1.0 - pow(vUV.y, 1.5); // Nicht-lineares Tiefenprofil
                vec4 finalColor = mix(blueish, vec4(waterColor * 0.5, 1.0), depth * fogDensity);

                // Aufsteigende Luftblasen hinzufügen
                float bubble = bubbles(vUV, time);
                finalColor += vec4(bubble, bubble, bubble, 0.0);

                // Helligkeit leicht anpassen für bessere Unterwasserstimmung
                finalColor.rgb *= 0.9;

                // Leichten Vignette-Effekt hinzufügen
                float vignetteStrength = 0.15;
                vec2 vignettePos = vUV * (1.0 - vUV);
                float vignette = vignettePos.x * vignettePos.y * 15.0;
                vignette = pow(vignette, vignetteStrength);
                finalColor.rgb *= vignette;

                gl_FragColor = finalColor;
            }
        `;
  }

  private createUnderwaterEffect(): void {
    this.underwaterPostProcess = new PostProcess(
      'underwaterEffect',
      'underwater',
      ['time', 'waterColor', 'distortionStrength', 'fogDensity'],
      null,
      1.0,
      this.level.cameraController.camera
    );

    this.underwaterPostProcess.onApply = (effect) => {
      effect.setFloat('time', this.level.game.scene.getEngine().getDeltaTime() * 0.01 + this.level.game.scene.getAnimationRatio() * 0.5);
      effect.setFloat3('waterColor', 0.0, 0.2, 0.4);
      effect.setFloat('distortionStrength', 0.15);
      effect.setFloat('fogDensity', 0.5);
    };
  }

  private removeUnderwaterEffect(): void {
    if (this.underwaterPostProcess) {
      this.underwaterPostProcess.dispose();
      this.underwaterPostProcess = null;
    }
  }

  private checkUnderwaterStatus(): void {
    if (!this.level.player) {
      return;
    }
    const isUnderWaterNow = this.level.player.getPosition().y < this.level.config.water.level;

    if (isUnderWaterNow !== this.isUnderwater) {
      this.isUnderwater = isUnderWaterNow;

      if (isUnderWaterNow) {
        this.createUnderwaterEffect();
      } else {
        this.removeUnderwaterEffect();
      }
    }
  }

  public dispose(): void {
    this.removeUnderwaterEffect();
  }
}
